import React, { useMemo } from "react";
import { useViewport } from 'components/general/useViewPort'
import MirsTable from "components/general/table/Table";
import { TableExpanded } from "components/sports/glo/poolstanding/components/TableExpanded";
import { ColumnRankNoResult, ColumnName, ColumnNoc, ColumnExpander } from "components/common/TableColumns"
import Label from "components/general/Label"
import LoaderSubComponent from "components/general/LoaderSubComponent";
import Competitor from 'components/presentation/Competitor';
import { NocMemo as Noc } from 'components/presentation/Noc';
import { findArray } from "helpers/utils";
import { useUrlFriendlyToParameters } from "helpers/urlFriendly";
import { createColumns, CaptionTable } from "helpers/table";

function ColumnSeeding({
    disableSort = false,
    className = 'text-end',
    headerClassName = 'text-end w-5 text-nowrap',
    label = 'HeaderSeeding',
    breakPoint
}) {
    if (breakPoint === 'sm' || breakPoint === 'xs')
        label = 'HeaderS'
    return (
        {
            Header: <Label data={label} legendFixed={true} />,
            accessor: 'sortOrder',
            className: className,
            headerClassName: headerClassName,
            disableSortBy: disableSort,
            id: 'RankNoResult',
            Cell: (props) => {
                let group = findArray(props.cell.row.original.participant.registeredEvents?.[0].eventEntries, { ee_code: ['GROUP'] }).ee_value;
                let rank = props.cell.row.original.sortOrder
                return (
                    <>
                        <PlayerStatsType group={group} rank={rank} />
                    </>
                )
            }
        }
    )
}

function getColumns(cols, isCombinedRanking, discipline, breakPoint, status) {

    if (['RU7'].includes(discipline) && isCombinedRanking) {
        const columns =
            [
                ColumnSeeding({ disableSort: true, breakPoint }),
                ColumnNocRU7({ disableSort: true, status: status }),
                ColumnNameRU7({ label: 'HeaderTeam', disableSort: true, status: status }),
                ...createColumns(cols, discipline),
            ]
        if (!isCombinedRanking && breakPoint !== 'sm' && breakPoint !== 'xs') {
            columns.push(ColumnExpander({ type: 'alwaysTrue' }))
        }
        return columns
    }
    else {
        let showSeed = ['BDM'].includes(discipline)
        const columns =
            [
                ColumnRankNoResult({ disableSort: true, isPoolStandings: true }),
                ColumnNoc({ disableSort: true }),
                ColumnName({ disableSort: true, showSeed: showSeed }),
                ...createColumns(cols, discipline),
            ]
        if (!isCombinedRanking && breakPoint !== 'sm' && breakPoint !== 'xs') {
            let type = 'alwaysTrue2'
            let expandIrm = false
            if (['FBL', 'HOC'].includes(discipline)) {
                type = 'alwaysTrue'
                expandIrm = true
            }
            columns.push(ColumnExpander({ type: type, isPoolStandings: true, expandIrm: expandIrm }))
        }
        return columns
    }
}






const PoolsStanding = ({ data, columns, isCombinedRanking }) => {
    const { mirs_discipline } = useUrlFriendlyToParameters()
    const { breakPoint } = useViewport();
    const status = data?.status
    const _columns = useMemo(() => getColumns(columns, isCombinedRanking, mirs_discipline, breakPoint, data?.status), [breakPoint, status]);
    const renderRowSubComponent = React.useCallback(({ row }) => {
        //let cmp = <TableExpanded data={row?.original} />
        return (
            <LoaderSubComponent component={<TableExpanded data={row?.original} />} />
        )
    }, []);

    //add columns in data in order to can call from configuration to draw in table
    data && data?.results.forEach((res) => {
        switch (mirs_discipline) {
            case 'BDM':
                res.gamesW = findArray(res?.extendedResults, { er_code: ['GAMES'], er_type: ["ER"] }).er_value;
                res.gamesL = findArray(findArray(res?.extendedResults, { er_code: ['GAMES'], er_type: ["ER"] }).extensions, { ere_code: ['LOST'] }).ere_value
                res.gamesDiff = findArray(res?.extendedResults, { er_code: ['GAMES'], er_type: ["ER"] }).er_diff;
                break
            case 'WBK':
            case 'BKB':
                const won = res?.won !== undefined ? String(res?.won) : ''
                const lost = res?.lost !== undefined ? String(res?.lost) : ''
                res.winlost = (won || lost) && won + '/' + lost
                res.group = findArray(res?.extendedResults, { er_code: ['SUB_RANK'], er_type: ["ER"] }).er_value;
                break
            case 'BK3':
                res.pForAvg = findArray(res?.extendedResults, { er_code: ['PTS_AVG'], er_type: ["ER"] }).er_value;
                break
            case 'BSC':
                res.WE = findArray(res?.extendedResults, { er_code: ['WE'], er_type: ["ER"] }).er_value;
                res.WP = findArray(res?.extendedResults, { er_code: ['WP'], er_type: ["ER"] }).er_value;
                break
            case 'BOC':
                res.pdm = findArray(res?.extendedResults, { er_code: ['PDM'] })?.er_value;
                res.pde = findArray(res?.extendedResults, { er_code: ['PDE'] })?.er_value;
                res.eWon = findArray(res?.extendedResults, { er_code: ['ENDS'] })?.er_value;
                break
            case 'FBL':
                res.FPP = findArray(res?.extendedResults, { er_code: ['FPP'], er_type: ["ER"] }).er_value;
                break
            case 'KTE':
                const _for = stringOrEmpty(res?.pFor)
                const _against = stringOrEmpty(res?.pAgainst) !== undefined ? String(res?.pAgainst) : ''
                const score = findArray(res?.extendedResults, { er_code: ['SCORE'] })
                const score__against = findArray(res?.extendedResults, { er_code: ['SCORE_AGAINST'] })
                const IPP = findArray(score?.extensions, { ere_code: ['IPP'] })?.ere_value;
                const WAZ = findArray(score?.extensions, { ere_code: ['WAZ'] })?.ere_value;
                const YUK = findArray(score?.extensions, { ere_code: ['YUK'] })?.ere_value;
                const IPP_AGAINST = findArray(score__against?.extensions, { ere_code: ['IPP'] })?.ere_value;
                const WAZ_AGAINST = findArray(score__against?.extensions, { ere_code: ['WAZ'] })?.ere_value;
                const YUK_AGAINST = findArray(score__against?.extensions, { ere_code: ['YUK'] })?.ere_value;
                let kk = 1
                res.total = (_for || _against) && _for + ':' + _against;
                res.ipp = (stringOrEmpty(IPP) || stringOrEmpty(IPP_AGAINST)) && IPP + ':' + IPP_AGAINST
                res.waz = (stringOrEmpty(WAZ) || stringOrEmpty(WAZ_AGAINST)) && WAZ + ':' + WAZ_AGAINST
                res.yuk = (stringOrEmpty(YUK) || stringOrEmpty(YUK_AGAINST)) && YUK + ':' + YUK_AGAINST
                break
            case 'RU7':
                res.triesFor = findArray(res?.extendedResults?.[0]?.extensions, { ere_code: ['WON'] }).ere_value;
                res.triesAgainst = findArray(res?.extendedResults?.[0]?.extensions, { ere_code: ['LOST'] }).ere_value;
                res.triesDiff = findArray(res?.extendedResults?.[0]?.extensions, { ere_code: ['DIFF'] }).ere_value;
                res.poolRank = findArray(res?.extendedResults, { er_code: ['SUB_RANK'] }).er_value;
                res.subRank = findArray(res?.participant?.registeredEvents?.[0]?.eventEntries, { ee_code: ['SEED'] }).ee_value;
                res.subRankKO = (res.rank > 6) ? res.subRank : '';
                break
            case 'HBB':
                res.setFor = findArray(res?.extendedResults, { er_pos: ['WON'], er_code: ['PERIOD'] }).er_value;
                res.setAgainst = findArray(res?.extendedResults, { er_pos: ['LOST'], er_code: ['PERIOD'] }).er_value;
                res.setDiff = findArray(res?.extendedResults, { er_pos: ['DIFF'], er_code: ['PERIOD'] }).er_value;
                break
            case 'VBV':
            case 'VBS':
            case 'VVO':
                res.ptsW = findArray(findArray(res?.extendedResults, { er_code: ['SUB_RES'] }).extensions, { ere_code: ['WON'] }).ere_value
                res.ptsL = findArray(findArray(res?.extendedResults, { er_code: ['SUB_RES'] }).extensions, { ere_code: ['LOST'] }).ere_value
                res.ptsRatio = findArray(findArray(res?.extendedResults, { er_code: ['SUB_RES'] }).extensions, { ere_code: ['RATIO'] }).ere_value
                res.grpRank = findArray(res?.extendedResults, { er_code: ['SUB_RANK'] }).er_value
                break
            case 'TEQ':
                res.setsW = findArray(res?.extendedResults, { er_code: ['SETS'] }).er_value
                res.setsL = findArray(findArray(res?.extendedResults, { er_code: ['SETS'] }).extensions, { ere_code: ['LOST'] }).ere_value
                res.setsDiff = findArray(res?.extendedResults, { er_code: ['SETS'] }).er_value
                break
            case 'TTE':
                res.won = findArray(findArray(res?.extendedResults, { er_code: ['MATCHES'] }).extensions, { ere_code: ['WON'] }).ere_value
                res.lost = findArray(findArray(res?.extendedResults, { er_code: ['MATCHES'] }).extensions, { ere_code: ['LOST'] }).ere_value
                res.played = findArray(findArray(res?.extendedResults, { er_code: ['MATCHES'] }).extensions, { ere_code: ['PLAYED'] }).ere_value
                res.gamesW = findArray(findArray(res?.extendedResults, { er_code: ['GAMES'] }).extensions, { ere_code: ['WON'] }).ere_value
                res.gamesL = findArray(findArray(res?.extendedResults, { er_code: ['GAMES'] }).extensions, { ere_code: ['LOST'] }).ere_value
                res.pointsW = findArray(findArray(res?.extendedResults, { er_code: ['POINTS'] }).extensions, { ere_code: ['WON'] }).ere_value
                res.pointsL = findArray(findArray(res?.extendedResults, { er_code: ['POINTS'] }).extensions, { ere_code: ['LOST'] }).ere_value
                break
            case 'WPO':
                res.psWon = findArray(res?.extendedResults, { er_code: ['PSW'] })?.er_value;
                res.psLost = findArray(res?.extendedResults, { er_code: ['PSL'] })?.er_value;
                if (res?.irm === "DSQ" || res?.irm === "DQB") {
                    res.pFor = ''
                    res.pDiff = ''
                    res.lost = ''
                    res.pAgainst = ''
                    res.played = ''
                    res.won = ''

                }

                break

            default:
                //nothing to do
                break
        }
    })

    let hiddenColumns = []
    let showQualMark = data?.results?.some(item => item.qualificationMark !== undefined);

    let arrQualMark = ['BK3', 'BKG', 'BDM', 'BSC', 'VBV', 'VBS', 'VVO', 'VBV', 'WPO']
    let arrShortDescription = ['BDM', 'BKG', 'VBV']

    if (arrQualMark.includes(mirs_discipline)) {
        showQualMark = data?.results?.some(item => item.qualificationMark);
    }
    if (!showQualMark) hiddenColumns.push('QualificationMark')


    let captionHeader = data?.phase?.description
    if (arrShortDescription.includes(mirs_discipline)) {
        captionHeader = data?.phase?.shortDescription
    }

    return (
        <>
            {data &&
                < MirsTable
                    className='table-pool'
                    data={data.results}
                    caption={<CaptionTable caption={captionHeader} />}
                    columns={_columns}
                    renderRowSubComponent={renderRowSubComponent}
                    hiddenColumns={hiddenColumns}
                    id={`mirs-table-pool-standing-${data.rsc}`}
                    keyRow='participant-code'
                    initialSortBy={[{ id: 'RankNoResult', desc: false }]}//Id of column

                />
            }
        </>
    )
};
export default PoolsStanding;

function PlayerStatsType({ group, rank }) {
    if (group !== undefined && rank <= 6) {
        switch (rank) {
            case 1: case 2: case 3:
                return (
                    <>
                        {group}1
                    </>
                )
            case 4: case 5: case 6:
                return (
                    <>
                        {group}2
                    </>
                )
        }
    }
    else {
        return (
            <>
                {rank}
            </>
        )
    }
}



/**Special configurations columns */

function ColumnNocRU7({
    disableSort = false,
    className = 'text-start d-none d-md-table-cell ',
    headerClassName = 'text-start d-none d-md-table-cell w-5',
    label = 'HeaderNOC',
    status = ''
}) {
    return (
        {
            Header: <Label data={label} legendFixed={true} />,
            id: 'NOC',
            accessor: 'participant.organisation.code',
            className: className,
            headerClassName: headerClassName,
            disableSortBy: disableSort, //habilita poder ordenar
            Cell: (props) => {
                const row = props.cell.row.original
                let data
                if (row?.athletes) {
                    data = row?.athletes?.find(x => x !== undefined)?.participant?.organisation
                } else {
                    data = row?.participant?.organisation
                }

                return (status !== 'START_LIST' && <Noc {...data} />)
            }
        }
    )

}

function ColumnNameRU7({
    disableSort = false,
    className = 'text-start',
    headerClassName = 'text-start',
    label = 'HeaderName',
    hideNoc = false,
    status = ''
}) {


    return (
        {
            Header: (props) => {
                const row = props?.data[0]

                let lbl
                if (label !== 'HeaderName') {
                    lbl = label
                } else {
                    lbl = row?.participant?.__typename === 'Person' ? 'HeaderName' : 'HeaderTeam'
                }

                return (
                    <Label data={lbl} />
                )
            },
            accessor: (props) => (props?.participant?.name?.toUpperCase()),
            id: 'Name',
            className: className,
            headerClassName: headerClassName,
            disableSortBy: disableSort, //habilita poder ordenar
            hideNoc: hideNoc,
            Cell: (props) => {
                const row = props.cell.row.original
                let data
                if (row?.athletes) {
                    data = row?.athletes?.find(x => x !== undefined)?.participant
                } else {
                    data = row?.participant
                }

                return (status !== 'START_LIST' && <Competitor
                    hidePicture={true}
                    hideNoc={hideNoc}
                    {...data}
                />)
            }
        }
    )
}

function stringOrEmpty(value) {
    return (
        value !== undefined ? String(value) : ''
    )
}



